/**
 * Connecting Claude or ChatGPT to this company's books.
 *
 * Two things live here, and the order matters: the instructions come first,
 * because the hard part of an MCP integration is not clicking "create" — it is
 * knowing where the URL goes in a client you have never configured before. A
 * key with no instructions beside it is a string somebody pastes into the
 * wrong box.
 *
 * The secret is shown exactly once, at creation, and the panel says so plainly
 * before the button is pressed rather than after. There is no column holding
 * the plaintext and therefore no endpoint that could show it again; a lost key
 * is revoked and reissued, which is both the only safe answer and the fastest.
 *
 * The capabilities, limits, starters and example sentences are imported rather
 * than written here. They used to be local arrays, which meant this panel, the
 * assistant's own answer to "what can you do?", and the guide at `/assistant`
 * were three descriptions of one feature set, kept in step by remembering to.
 * See the note at the top of `mcp-guide.ts`: a test now ties every entry to a
 * tool the server really serves, which is a guarantee a local array could not
 * offer.
 */
import { MCP_CAPABILITIES, MCP_EXAMPLES, MCP_LIMITS, MCP_STARTERS } from "@dantria/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, apiErrorMessage, unwrap } from "../api";
import { Button, Empty, ErrorNote, Field, Loading, Panel, Table, Td, Th, input } from "../ui";

/** Where this deployment actually answers. Read from the browser rather than
 *  hard-coded, so a staging tenant prints its own address and nobody connects
 *  a desktop client to production by following the wrong instructions. */
const serverUrl = () => `${window.location.origin}/api/mcp`;

/**
 * `prose` switches off the monospace treatment.
 *
 * A URL or a shell command has to be shown exactly, character for character,
 * which is what `whitespace-pre` and a mono face are for. A sentence somebody
 * is meant to say is the opposite: set in mono and refusing to wrap, it reads
 * as configuration to be transcribed rather than as English to be spoken, and
 * a long one scrolls sideways out of the panel.
 */
function Copyable({
  value,
  label,
  prose = false,
}: {
  value: string;
  label: string;
  prose?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-stretch gap-2">
      <code
        className={
          prose
            ? "min-w-0 flex-1 rounded-lg bg-neutral-100 px-3 py-2 font-sans text-neutral-800 text-sm"
            : "min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-lg bg-neutral-900 px-3 py-2 font-mono text-neutral-100 text-xs"
        }
      >
        {value}
      </code>
      <Button
        variant="ghost"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
          );
        }}
      >
        {copied ? "Copied" : `Copy ${label}`.trim()}
      </Button>
    </div>
  );
}

/**
 * Prove the key works, here, before it is pasted anywhere.
 *
 * Without this the panel shows a secret once and wishes the operator luck: the
 * next thing they learn is whether a client they have never configured reports
 * a useful error, and the failure modes there (wrong box, truncated paste, a
 * header the client formats differently) all look identical from the outside.
 * One handshake against the real endpoint separates "the key is bad" from
 * "the client is misconfigured", which is the question worth answering while
 * the key is still on screen.
 *
 * It speaks MCP rather than calling some private health route, because the
 * thing being tested is precisely that: a bearer token completing a real
 * handshake and getting back a real tool list.
 */
function TestConnection({ token }: { token: string }) {
  const [state, setState] = useState<
    | { status: "idle" | "testing" }
    | { status: "ok"; tools: number }
    | { status: "failed"; why: string }
  >({ status: "idle" });

  async function run() {
    setState({ status: "testing" });
    try {
      const res = await fetch(serverUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method: "initialize",
          params: {
            protocolVersion: "2025-06-18",
            capabilities: {},
            clientInfo: { name: "dantria-settings", version: "1" },
          },
        }),
      });
      if (res.status === 401) {
        setState({ status: "failed", why: "The server didn't recognise that key." });
        return;
      }
      if (!res.ok) {
        setState({ status: "failed", why: `The server answered ${res.status}.` });
        return;
      }

      const listed = await fetch(serverUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ jsonrpc: "2.0", id: 2, method: "tools/list" }),
      });
      const body = (await listed.json()) as { result?: { tools?: unknown[] } };
      const tools = body.result?.tools?.length ?? 0;
      if (tools === 0) {
        setState({ status: "failed", why: "It connected but offered no tools." });
        return;
      }
      setState({ status: "ok", tools });
    } catch {
      // A network failure here is the browser's, not the key's — saying "bad
      // key" would send somebody to reissue a credential that was fine.
      setState({ status: "failed", why: "Couldn't reach the server from this browser." });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button variant="ghost" onClick={run} disabled={state.status === "testing"}>
        {state.status === "testing" ? "Testing…" : "Test connection"}
      </Button>
      {state.status === "ok" && (
        <span className="text-emerald-800 text-sm">Connected — {state.tools} tools available.</span>
      )}
      {state.status === "failed" && <span className="text-red-700 text-sm">{state.why}</span>}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-neutral-900 font-semibold text-white text-xs">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2 pt-0.5">
        <p className="font-medium text-neutral-900 text-sm">{title}</p>
        <div className="space-y-2 text-neutral-600 text-sm">{children}</div>
      </div>
    </li>
  );
}

export function IntegrationsPanel({ slug, isOwner }: { slug: string; isOwner: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [allowApprove, setAllowApprove] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const keys = useQuery({
    queryKey: ["mcp-keys", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"]["mcp-keys"].$get({ param: { slug } })),
    enabled: isOwner,
  });

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"]["mcp-keys"].$post({
          param: { slug },
          json: { name: name.trim() || "Assistant", allowApprove },
        }),
      ),
    onSuccess: (data) => {
      setIssued(data.token);
      setName("");
      setAllowApprove(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ["mcp-keys", slug] });
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.api.c[":slug"]["mcp-keys"][":id"].$delete({ param: { slug, id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-keys", slug] }),
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const url = serverUrl();

  return (
    <div className="space-y-6">
      <Panel title="Connect Claude or ChatGPT">
        <div className="space-y-6">
          <p className="text-neutral-600 text-sm">
            Let an assistant price work against your own rates, draft the proposal, and hand you
            back a link that downloads the PDF.
          </p>
          <div>
            <p className="mb-3 font-medium text-neutral-900 text-sm">What it can do</p>
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {MCP_CAPABILITIES.map((capability) => (
                <div key={capability.what}>
                  <dt className="font-medium text-neutral-900 text-sm">{capability.what}</dt>
                  <dd className="text-neutral-600 text-sm">{capability.detail}</dd>
                </div>
              ))}
            </dl>
            <ul className="mt-3 space-y-1 text-neutral-500 text-sm">
              {MCP_LIMITS.map((limit) => (
                <li key={limit}>{limit}</li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-3 font-medium text-neutral-900 text-sm">
              Ready-made starters, once you're connected
            </p>
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {MCP_STARTERS.map((starter) => (
                <div key={starter.name}>
                  <dt className="font-medium text-neutral-900 text-sm">{starter.title}</dt>
                  <dd className="text-neutral-600 text-sm">{starter.detail}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-neutral-500 text-sm">
              Pick one from your assistant instead of typing the whole request — it walks the steps
              in the right order, including reading your price book before it quotes.
            </p>
          </div>

          <div>
            <p className="mb-1 font-medium text-neutral-900 text-sm">Things to say</p>
            <p className="mb-3 text-neutral-600 text-sm">
              Say the whole thing in one go — there's no need to break it into steps.
            </p>
            <ul className="space-y-2">
              {MCP_EXAMPLES.map((example) => (
                <li key={example.text}>
                  <p className="mb-1 text-neutral-500 text-xs uppercase tracking-wide">
                    {example.about}
                  </p>
                  <Copyable value={example.text} label="" prose />
                </li>
              ))}
            </ul>
            <p className="mt-3 text-neutral-500 text-sm">
              There's a{" "}
              <a className="underline" href="/assistant">
                fuller guide
              </a>{" "}
              you can send to anyone on your team.
            </p>
          </div>

          <div>
            <p className="mb-3 font-medium text-neutral-900 text-sm">Your server address</p>
            <Copyable value={url} label="URL" />
          </div>

          <div>
            <p className="mb-3 font-medium text-neutral-900 text-sm">Claude Code</p>
            <ol className="space-y-4">
              <Step n={1} title="Create a key below, and copy it.">
                <p>You'll only see it once.</p>
              </Step>
              <Step n={2} title="Run this in your terminal.">
                <Copyable
                  value={`claude mcp add --transport http dantria ${url} --header "Authorization: Bearer YOUR_KEY"`}
                  label="command"
                />
                <p>Replace YOUR_KEY with the key you copied.</p>
              </Step>
              <Step n={3} title="Ask for a quote.">
                <p className="italic">
                  “Quote the Harper Residence, hello@harper.com — a kitchen with 3 rows of cabinets
                  at 89 inches, stone at 85 inches with a 20 inch backsplash and a 2 inch mitre. Use
                  my prices. Bill it 30/40/30.”
                </p>
              </Step>
            </ol>
          </div>

          <div>
            <p className="mb-3 font-medium text-neutral-900 text-sm">
              Claude (desktop and web) and ChatGPT
            </p>
            <ol className="space-y-4">
              <Step n={1} title="Open your assistant's connector settings.">
                <p>
                  In Claude: Settings → Connectors → Add custom connector. In ChatGPT: Settings →
                  Connectors → Add.
                </p>
              </Step>
              <Step n={2} title="Paste the server address above. Nothing else.">
                <p>
                  No key, no client ID — the connector registers itself and sends you here to sign
                  in.
                </p>
              </Step>
              <Step n={3} title="Approve the connection.">
                <p>
                  You'll pick which company it may act on, and whether it may approve contracts.
                  Only an owner can approve a connection.
                </p>
              </Step>
            </ol>
          </div>
        </div>
      </Panel>

      {!isOwner ? (
        <Panel title="Keys">
          <Empty title="Owners only">
            Only an owner can issue or revoke the keys an assistant connects with.
          </Empty>
        </Panel>
      ) : (
        <Panel title="Keys">
          <div className="space-y-4">
            <p className="text-neutral-600 text-sm">
              A key acts as this company, with nobody watching. Issue one per assistant, and revoke
              it the moment it isn't needed.
            </p>
            {error && <ErrorNote>{error}</ErrorNote>}

            {issued && (
              <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="font-medium text-emerald-900 text-sm">
                  Copy this now — it will never be shown again.
                </p>
                <Copyable value={issued} label="key" />
                <TestConnection token={issued} />
                <button
                  type="button"
                  className="text-emerald-800 text-sm underline"
                  onClick={() => setIssued(null)}
                >
                  I've saved it
                </button>
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-52 flex-1">
                <Field label="What is this key for?">
                  <input
                    className={input}
                    value={name}
                    placeholder="Claude Desktop"
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>
              </div>
              <label className="flex items-center gap-2 pb-2 text-neutral-700 text-sm">
                <input
                  type="checkbox"
                  checked={allowApprove}
                  onChange={(e) => setAllowApprove(e.target.checked)}
                />
                Allow approving contracts
              </label>
              <div className="pb-1">
                <Button onClick={() => create.mutate()} disabled={create.isPending}>
                  {create.isPending ? "Creating…" : "Create key"}
                </Button>
              </div>
            </div>
            <p className="text-neutral-500 text-sm">
              Approving turns a signed contract into live projects, milestones and commitments, and
              posts to your books. Leave it off unless you mean it.
            </p>

            {keys.isPending ? (
              <Loading />
            ) : keys.data?.keys.length === 0 ? (
              <Empty title="No keys yet">
                Create one above, then paste it into your assistant.
              </Empty>
            ) : (
              <Table
                head={
                  <tr>
                    <Th>Name</Th>
                    <Th>Key</Th>
                    <Th>Approve</Th>
                    <Th>Last used</Th>
                    <Th> </Th>
                  </tr>
                }
              >
                {keys.data?.keys.map((k) => (
                  <tr key={k.id}>
                    <Td>{k.name}</Td>
                    <Td>
                      <code className="font-mono text-xs">{k.prefix}…</code>
                    </Td>
                    <Td>{k.allowApprove ? "Yes" : "No"}</Td>
                    <Td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : "Never"}</Td>
                    <Td>
                      <Button
                        variant="ghost"
                        onClick={() => revoke.mutate(k.id)}
                        disabled={revoke.isPending}
                      >
                        Revoke
                      </Button>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
